import { isCalendarDate } from "../utils/assessment_task_date.util";
import { isAssessmentTaskStatus } from "../utils/assessment_task_status.util";
import type {
    AssessmentTaskActor, AssessmentTaskAssignee, AssessmentTaskAssessmentOption,
    AssessmentTaskAssigneeOption, AssessmentTaskComment, AssessmentTaskCommentDeleteReceipt,
    AssessmentTaskCommentMutationReceipt, AssessmentTaskCount, AssessmentTaskCreationAssigneeOption,
    AssessmentTaskCreationOptions, AssessmentTaskDeleteReceipt, AssessmentTaskEditOptions,
    AssessmentTaskMutationReceipt,
    AssessmentTaskOffsetPage, AssessmentTaskPage, AssessmentTaskRequirement,
    AssessmentTaskRequirementOption, AssessmentTaskStatus, AssessmentTaskStatusOption, AssessmentTaskSummary
} from "./assessment_tasks.types";

export interface AssessmentTaskApiResponse {
    success: boolean;
    data?: unknown;
    code?: string;
    message?: string;
}

export class AssessmentTaskApiError extends Error {
    constructor(message: string, readonly code: string | null = null) {
        super(message);
        this.name = "AssessmentTaskApiError";
    }
}

export function requireTaskApiData(response: AssessmentTaskApiResponse, fallbackMessage: string): unknown {
    if (response?.success !== true || response.data === undefined || response.data === null) {
        const message = typeof response?.message === "string" && response.message.trim()
            ? response.message : fallbackMessage;
        const code = typeof response?.code === "string" ? response.code : null;
        throw new AssessmentTaskApiError(message, code);
    }
    return response.data;
}

function record(value: unknown, name: string): Record<string, unknown> {
    if (!value || typeof value !== "object" || Array.isArray(value)) {
        throw new AssessmentTaskApiError(`Invalid ${name} response.`);
    }
    return value as Record<string, unknown>;
}

function positiveInteger(value: unknown, name: string): number {
    if (typeof value !== "number" || !Number.isSafeInteger(value) || value <= 0) {
        throw new AssessmentTaskApiError(`Invalid ${name} response.`);
    }
    return value;
}

function nonnegativeInteger(value: unknown, name: string): number {
    if (typeof value !== "number" || !Number.isSafeInteger(value) || value < 0) {
        throw new AssessmentTaskApiError(`Invalid ${name} response.`);
    }
    return value;
}

function stringValue(value: unknown, name: string): string {
    if (typeof value !== "string" || !value.trim()) {
        throw new AssessmentTaskApiError(`Invalid ${name} response.`);
    }
    return value;
}

function booleanValue(value: unknown, name: string): boolean {
    if (typeof value !== "boolean") throw new AssessmentTaskApiError(`Invalid ${name} response.`);
    return value;
}

function timestamp(value: unknown, name: string): string | null {
    if (value === null) return null;
    if (typeof value !== "string" ||
        !/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})$/.test(value) ||
        !Number.isFinite(Date.parse(value))) {
        throw new AssessmentTaskApiError(`Invalid ${name} response.`);
    }
    return value;
}

function dueDate(value: unknown): string | null {
    if (value === null) return null;
    if (!isCalendarDate(value)) {
        throw new AssessmentTaskApiError("Invalid task due date response.");
    }
    return value;
}

function arrayOf<T>(value: unknown, name: string, parse: (item: unknown) => T): T[] {
    if (!Array.isArray(value)) throw new AssessmentTaskApiError(`Invalid ${name} response.`);
    return value.map(parse);
}

function actor(value: unknown): AssessmentTaskActor {
    const item = record(value, "task actor");
    return {
        userId: positiveInteger(item["userId"], "task actor"),
        displayName: stringValue(item["displayName"], "task actor name")
    };
}

function optionalActor(value: unknown): AssessmentTaskActor | null {
    return value === null ? null : actor(value);
}

function requirement(value: unknown): AssessmentTaskRequirement {
    const item = record(value, "task requirement");
    return {
        sectionKey: stringValue(item["sectionKey"], "section key"),
        requirementKey: stringValue(item["requirementKey"], "requirement key"),
        sectionLabel: stringValue(item["sectionLabel"], "section label"),
        requirementLabel: stringValue(item["requirementLabel"], "requirement label"),
        navigationSectionKey: stringValue(item["navigationSectionKey"], "navigation section"),
        navigationRequirementKey: stringValue(item["navigationRequirementKey"], "navigation requirement")
    };
}

function assignee(value: unknown): AssessmentTaskAssignee {
    const item = record(value, "task assignee");
    return {
        ...actor(item),
        assignedAt: timestamp(item["assignedAt"], "assignment time"),
        assignedBy: optionalActor(item["assignedBy"])
    };
}

export function requireTaskSummary(value: unknown): AssessmentTaskSummary {
    const item = record(value, "task");
    const status = item["status"];
    if (!isAssessmentTaskStatus(status)) throw new AssessmentTaskApiError("Invalid task status response.");
    const capabilities = record(item["capabilities"], "task capabilities");
    const requirements = arrayOf(item["requirements"], "task requirements", requirement);
    return {
        id: positiveInteger(item["id"], "task ID"),
        productLicenseId: positiveInteger(item["productLicenseId"], "assessment ID"),
        assessmentLabel: stringValue(item["assessmentLabel"], "assessment label"),
        description: stringValue(item["description"], "task description"),
        status,
        dueDate: dueDate(item["dueDate"]),
        creator: optionalActor(item["creator"]),
        assignees: arrayOf(item["assignees"], "task assignees", assignee),
        requirements,
        lastStatusChangedAt: timestamp(item["lastStatusChangedAt"], "status time"),
        lastStatusChangedBy: optionalActor(item["lastStatusChangedBy"]),
        updatedAt: timestamp(item["updatedAt"], "task update time"),
        createdAt: timestamp(item["createdAt"], "task creation time"),
        revision: nonnegativeInteger(item["revision"], "task revision"),
        capabilities: {
            canView: booleanValue(capabilities["canView"], "task view capability"),
            canChangeStatus: booleanValue(capabilities["canChangeStatus"], "task status capability"),
            canAddComment: booleanValue(capabilities["canAddComment"], "task comment capability"),
            canEditTask: booleanValue(capabilities["canEditTask"], "task edit capability"),
            canDeleteTask: booleanValue(capabilities["canDeleteTask"], "task delete capability")
        }
    };
}

export function requireTaskComment(value: unknown): AssessmentTaskComment {
    const item = record(value, "task comment");
    const capabilities = record(item["capabilities"], "comment capabilities");
    return {
        id: positiveInteger(item["id"], "comment ID"),
        taskId: positiveInteger(item["taskId"], "comment task ID"),
        body: stringValue(item["body"], "comment body"),
        revision: nonnegativeInteger(item["revision"], "comment revision"),
        author: optionalActor(item["author"]),
        createdAt: timestamp(item["createdAt"], "comment creation time"),
        updatedAt: timestamp(item["updatedAt"], "comment update time"),
        capabilities: {
            canEditOwnComment: booleanValue(capabilities["canEditOwnComment"], "comment edit capability"),
            canDeleteOwnComment: booleanValue(capabilities["canDeleteOwnComment"], "comment delete capability")
        }
    };
}

export function requireTaskPage<T>(value: unknown, parse: (item: unknown) => T): AssessmentTaskPage<T> {
    const page = record(value, "task page");
    return {
        items: arrayOf(page["items"], "task page items", parse),
        page: positiveInteger(page["page"], "task page number"),
        pageSize: positiveInteger(page["pageSize"], "task page size"),
        total: nonnegativeInteger(page["total"], "task page total")
    };
}

export function requireTaskOffsetPage<T>(value: unknown, parse: (item: unknown) => T): AssessmentTaskOffsetPage<T> {
    const page = record(value, "task options page");
    return {
        items: arrayOf(page["items"], "task options", parse),
        total: nonnegativeInteger(page["total"], "task options total"),
        offset: nonnegativeInteger(page["offset"], "task options offset"),
        limit: positiveInteger(page["limit"], "task options limit")
    };
}

export function requireTaskCount(value: unknown): AssessmentTaskCount {
    const item = record(value, "task count");
    return { count: nonnegativeInteger(item["count"], "task count") };
}

export function requireTaskStatusCounts(value: unknown): Record<AssessmentTaskStatus, number> {
    const counts = record(record(value, "task status counts")["counts"], "task status counts");
    return {
        needs_review: nonnegativeInteger(counts["needs_review"], "incomplete task count"),
        pending_review: nonnegativeInteger(counts["pending_review"], "pending task count"),
        rejected: nonnegativeInteger(counts["rejected"], "rejected task count"),
        approved: nonnegativeInteger(counts["approved"], "approved task count")
    };
}

export function requireAssessmentOption(value: unknown): AssessmentTaskAssessmentOption {
    const item = record(value, "assessment option");
    return {
        productLicenseId: positiveInteger(item["productLicenseId"], "assessment option ID"),
        label: stringValue(item["label"], "assessment option label")
    };
}

export function requireAssigneeOption(value: unknown): AssessmentTaskAssigneeOption {
    const item = record(value, "assignee option");
    return {
        userId: positiveInteger(item["userId"], "assignee option ID"),
        label: stringValue(item["label"], "assignee option label")
    };
}

export function requireRequirementOption(value: unknown): AssessmentTaskRequirementOption {
    const item = record(value, "requirement option");
    return {
        productLicenseId: positiveInteger(item["productLicenseId"], "requirement assessment ID"),
        sectionKey: stringValue(item["sectionKey"], "requirement section"),
        requirementKey: stringValue(item["requirementKey"], "requirement key"),
        sectionLabel: stringValue(item["sectionLabel"], "requirement section label"),
        requirementLabel: stringValue(item["requirementLabel"], "requirement label"),
        label: stringValue(item["label"], "requirement option label")
    };
}

export function requireStatusOption(value: unknown): AssessmentTaskStatusOption {
    const item = record(value, "status option");
    const status = item["status"];
    if (!isAssessmentTaskStatus(status)) throw new AssessmentTaskApiError("Invalid status option response.");
    return { status, label: stringValue(item["label"], "status option label") };
}

export function requireTaskCreationOptions(value: unknown): AssessmentTaskCreationOptions {
    const item = record(value, "task creation options");
    if (item["canCreate"] !== true) throw new AssessmentTaskApiError("Task creation is unavailable.");
    return {
        productLicenseId: positiveInteger(item["productLicenseId"], "creation assessment ID"),
        canCreate: true,
        requirements: requireTaskOffsetPage(item["requirements"], requirement),
        assignees: requireTaskOffsetPage(item["assignees"], (entry): AssessmentTaskCreationAssigneeOption => {
            const user = record(entry, "creation assignee");
            return {
                userId: positiveInteger(user["userId"], "creation assignee ID"),
                displayName: stringValue(user["displayName"], "creation assignee name")
            };
        })
    };
}

export function requireTaskEditOptions(value: unknown): AssessmentTaskEditOptions {
    const item = record(value, "task edit options");
    if (item["canEdit"] !== true) throw new AssessmentTaskApiError("Task editing is unavailable.");
    return {
        taskId: positiveInteger(item["taskId"], "edit task ID"),
        productLicenseId: positiveInteger(item["productLicenseId"], "edit assessment ID"),
        revision: nonnegativeInteger(item["revision"], "edit task revision"),
        canEdit: true,
        requirements: requireTaskOffsetPage(item["requirements"], requirement),
        assignees: requireTaskOffsetPage(item["assignees"], (entry): AssessmentTaskCreationAssigneeOption => {
            const user = record(entry, "edit assignee");
            return {
                userId: positiveInteger(user["userId"], "edit assignee ID"),
                displayName: stringValue(user["displayName"], "edit assignee name")
            };
        })
    };
}

export function requireTaskMutationReceipt(value: unknown): AssessmentTaskMutationReceipt {
    const item = record(value, "task mutation");
    const id = positiveInteger(item["id"], "mutated task ID");
    const revision = nonnegativeInteger(item["revision"], "mutated task revision");
    const task = item["task"] === null ? null : requireTaskSummary(item["task"]);
    if (task && (task.id !== id || task.revision !== revision)) {
        throw new AssessmentTaskApiError("Invalid task mutation response.");
    }
    return { id, revision, changed: booleanValue(item["changed"], "task change flag"), task };
}

export function requireTaskDeleteReceipt(value: unknown): AssessmentTaskDeleteReceipt {
    const item = record(value, "task deletion");
    if (item["deleted"] !== true) throw new AssessmentTaskApiError("Invalid task deletion response.");
    return {
        id: positiveInteger(item["id"], "deleted task ID"),
        revision: nonnegativeInteger(item["revision"], "deleted task revision"),
        deleted: true
    };
}

export function requireCommentMutationReceipt(value: unknown): AssessmentTaskCommentMutationReceipt {
    const item = record(value, "comment mutation");
    const id = positiveInteger(item["id"], "mutated comment ID");
    const taskId = positiveInteger(item["taskId"], "mutated comment task ID");
    const revision = nonnegativeInteger(item["revision"], "mutated comment revision");
    const comment = item["comment"] === null ? null : requireTaskComment(item["comment"]);
    if (comment && (comment.id !== id || comment.taskId !== taskId || comment.revision !== revision)) {
        throw new AssessmentTaskApiError("Invalid comment mutation response.");
    }
    return {
        id, taskId, revision,
        taskRevision: nonnegativeInteger(item["taskRevision"], "parent task revision"),
        changed: booleanValue(item["changed"], "comment change flag"),
        comment
    };
}

export function requireCommentDeleteReceipt(value: unknown): AssessmentTaskCommentDeleteReceipt {
    const item = record(value, "comment deletion");
    if (item["deleted"] !== true) throw new AssessmentTaskApiError("Invalid comment deletion response.");
    return {
        id: positiveInteger(item["id"], "deleted comment ID"),
        taskId: positiveInteger(item["taskId"], "deleted comment task ID"),
        revision: nonnegativeInteger(item["revision"], "deleted comment revision"),
        taskRevision: nonnegativeInteger(item["taskRevision"], "parent task revision"),
        deleted: true
    };
}
